import { readonly, ref } from 'vue';

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary';

export interface ToastMessage {
	id?: number;
	severity?: ToastSeverity;
	summary?: string | null | undefined;
	detail?: string | null | undefined;
	life?: number;
	closable?: boolean;
	group?: string;
}

let nextToastId = 1;
const messages = ref<Array<ToastMessage & { id: number }>>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function remove(message: ToastMessage | number): void {
	const id = typeof message === 'number' ? message : message.id;
	if (id == null) return;
	const timer = timers.get(id);
	if (timer) clearTimeout(timer);
	timers.delete(id);
	messages.value = messages.value.filter((entry) => entry.id !== id);
}

function add(message: ToastMessage): ToastMessage & { id: number } {
	const entry = { severity: 'info' as const, closable: true, ...message, id: nextToastId++ };
	messages.value = [...messages.value, entry];
	if (entry.life && entry.life > 0)
		timers.set(
			entry.id,
			setTimeout(() => {
				remove(entry.id);
			}, entry.life)
		);
	return entry;
}

function removeGroup(group: string): void {
	messages.value.filter((message) => message.group === group).forEach(remove);
}

function removeAllGroups(): void {
	for (const message of messages.value) remove(message);
}

export const toastMessages = readonly(messages);

export function useToast() {
	return { add, remove, removeGroup, removeAllGroups };
}
